import type { UnitSystem } from '@/core/Units';
import {
  deserializeEnvironmentState,
  serializeEnvironmentState,
} from '@/environment/EnvironmentState';
import type { PlantGroup } from '@/editor/GroupManager';
import { FAUNA_ATTACHMENTS, type FaunaAttachment } from '@/fauna/FaunaDefinition';
import type { FaunaInstance } from '@/fauna/FaunaInstance';
import {
  MAX_OVERGROWN_MATURITY,
  PRUNING_STYLES,
  hasSiteOverrides,
  type FloraInstance,
  type PlantSiteOverrides,
  type PruningStyle,
  type Vec3,
} from '@/flora/FloraInstance';
import {
  deserializeLocalization,
  serializeLocalization,
  type SiteLocalization,
} from '@/localization/SiteLocalization';
import {
  PAVING_MATERIALS,
  ROOF_SHAPES,
  SITE_OBJECT_KINDS,
  clampExtent,
  normalizeMass,
  syncExtents,
  type BuildingMass,
  type PavingMaterial,
  type RoofShape,
  type SiteObject,
  type SiteObjectKind,
} from '@/site/SiteObject';
import { isValidRing, normalizeRing, type Point2, type Ring } from '@/site/SiteFootprint';
import {
  MAX_TAG_RADIUS_METERS,
  TAG_CATEGORIES,
  TAG_EMPHASES,
  cloneTag,
  normalizeLabel,
  normalizeNote,
  type SemanticTag,
  type TagCategory,
  type TagEmphasis,
  type TagTarget,
} from '@/semantics/SemanticTag';
import { parseTagImages } from '@/semantics/TagImage';
import {
  BASE_SURFACE_LAYER,
  BUILT_IN_PAINTED_LAYERS,
  MAX_PAINTED_LAYERS,
  normalizeHex,
  type SurfaceLayerState,
  type SurfacePaletteState,
} from '@/terrain/TerrainSurface';
import {
  TERRAIN_TEMPLATE_IDS,
  type TerrainGenerationParams,
  type TerrainProjectState,
  type TerrainTemplateId,
} from '@/terrain/TerrainTypes';
import {
  MAX_SPLAT_PAGES,
  MAX_SURFACE_RESOLUTION,
  type TerrainSurfaceState,
} from '@/terrain/TerrainSurface';
import {
  PROJECT_SCHEMA_VERSION,
  deriveAliasGroups,
  type LandscapeProject,
  type SavedCameraView,
} from './Project';

/**
 * (De)serialization for the canonical project document.
 *
 * Serialization is a plain deep copy — the document is already plain data, and copying it means a
 * saved record can never alias live editor state. Deserialization is defensive: a project file is
 * user-editable and may come from an older build, so every field is validated and anything
 * structurally wrong throws a {@link ProjectFormatError} naming the field, rather than producing a
 * half-loaded site (spec §20). Values that are merely out of range are clamped, never dropped.
 */

/** Thrown when a file cannot be read as a project. Carries a message meant for the user. */
export class ProjectFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProjectFormatError';
  }
}

/** Deep copy to a JSON-safe document, stamped with the current schema version. */
export function serializeProject(project: LandscapeProject): LandscapeProject {
  return {
    schemaVersion: PROJECT_SCHEMA_VERSION,
    id: project.id,
    name: project.name,
    ...(project.description !== undefined ? { description: project.description } : {}),
    createdAt: project.createdAt,
    updatedAt: project.updatedAt,
    units: project.units,
    terrain: serializeTerrainState(project.terrain),
    ...(project.surfacePalette && project.surfacePalette.layers.length > 0
      ? { surfacePalette: serializeSurfacePalette(project.surfacePalette) }
      : {}),
    environment: serializeEnvironmentState(project.environment),
    floraInstances: project.floraInstances.map(serializeFloraInstance),
    siteObjects: project.siteObjects.map(serializeSiteObject),
    faunaInstances: project.faunaInstances.map(serializeFaunaInstance),
    groups: project.groups.map((group) => ({
      id: group.id,
      name: group.name,
      memberIds: [...group.memberIds],
    })),
    aliasGroups: project.aliasGroups.map((group) => ({
      id: group.id,
      memberIds: [...group.memberIds],
    })),
    cameraViews: project.cameraViews.map((view) => ({
      id: view.id,
      name: view.name,
      position: { ...view.position },
      target: { ...view.target },
      fovDegrees: view.fovDegrees,
    })),
    semanticTags: project.semanticTags.map(cloneTag),
    ...(project.localization
      ? { localization: serializeLocalization(project.localization) }
      : {}),
    floraCatalogVersion: project.floraCatalogVersion,
  };
}

/** Rebuild a project from parsed JSON (a file, or an IndexedDB record written by an older build). */
export function deserializeProject(raw: unknown): LandscapeProject {
  const data = asRecord(raw, 'project');

  const schemaVersion = Math.trunc(Number(data.schemaVersion ?? PROJECT_SCHEMA_VERSION));
  if (!Number.isFinite(schemaVersion) || schemaVersion < 1) {
    throw new ProjectFormatError('This file has no valid project schema version.');
  }
  if (schemaVersion > PROJECT_SCHEMA_VERSION) {
    // Refusing beats guessing: a newer file may carry data this build would silently discard.
    throw new ProjectFormatError(
      `This site was saved by a newer version of Lush Glade (schema ${schemaVersion}; ` +
        `this build reads up to ${PROJECT_SCHEMA_VERSION}).`,
    );
  }

  const now = new Date().toISOString();
  const floraInstances = asArray(data.floraInstances, 'floraInstances').map((entry, index) =>
    deserializeFloraInstance(entry, index),
  );
  const siteObjects = migrateSiteObjects(schemaVersion, data.siteObjects);
  const faunaInstances = migrateFaunaInstances(schemaVersion, data.faunaInstances);
  const palette = deserializeSurfacePalette(data.surfacePalette);
  const description = typeof data.description === 'string' ? data.description : undefined;

  return {
    schemaVersion,
    id: text(data.id) ?? 'project-imported',
    name: text(data.name) ?? 'Imported site',
    ...(description !== undefined ? { description } : {}),
    createdAt: text(data.createdAt) ?? now,
    updatedAt: text(data.updatedAt) ?? now,
    units: data.units === 'metric' ? 'metric' : 'imperial',
    terrain: deserializeTerrainState(data.terrain),
    ...(palette.layers.length > 0 ? { surfacePalette: palette } : {}),
    environment: deserializeEnvironmentState(data.environment),
    floraInstances,
    siteObjects,
    faunaInstances,
    groups: deserializeGroups(data.groups, floraInstances),
    // Recomputed rather than trusted: the links live on the instances, which were just validated.
    aliasGroups: deriveAliasGroups(floraInstances),
    cameraViews: deserializeCameraViews(data.cameraViews),
    semanticTags: migrateSemanticTags(
      schemaVersion,
      data.semanticTags,
      floraInstances,
      siteObjects,
      palette,
    ),
    ...migrateLocalization(schemaVersion, data.localization),
    floraCatalogVersion: text(data.floraCatalogVersion) ?? 'unknown',
  };
}

/**
 * Schema 6 → 7: a site could not say where it was, so a v6 file has not been localized.
 *
 * The same statement `migrateSiteObjects` and `migrateSemanticTags` make about their own fields, and
 * for the same reason: "this document predates localization" and "this document was localized to
 * nothing" would both produce an absent profile, but only the first is honest about a v6 file. The
 * distinction is load-bearing here — an absent profile means no suitability has been checked, so the
 * palette draws no badges rather than badges that claim everything passed.
 *
 * A profile that fails to parse is dropped rather than fatal. Localization is a convenience laid over
 * a complete site; refusing to open a garden because its ZIP code was mangled would trade a
 * recoverable loss for a total one.
 */
function migrateLocalization(
  schemaVersion: number,
  raw: unknown,
): { localization?: SiteLocalization } {
  if (schemaVersion < 7) return {};
  if (raw === undefined || raw === null) return {};
  const localization = deserializeLocalization(raw);
  return localization ? { localization } : {};
}

/** Pretty-printed `.landscape.json` text, so an exported file stays diffable and hand-editable. */
export function toProjectJson(project: LandscapeProject): string {
  return `${JSON.stringify(serializeProject(project), null, 2)}\n`;
}

/** Parse `.landscape.json` text, mapping a syntax error to the same user-facing error type. */
export function parseProjectJson(json: string): LandscapeProject {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new ProjectFormatError('That file is not valid JSON.');
  }
  return deserializeProject(parsed);
}

/* ----------------------------- Ground materials ----------------------------- */

function serializeSurfacePalette(state: SurfacePaletteState): SurfacePaletteState {
  return {
    layers: state.layers.map((layer) => ({
      ...layer,
      ...(layer.swatch ? { swatch: { ...layer.swatch } } : {}),
      ...(layer.origin ? { origin: { ...layer.origin } } : {}),
    })),
  };
}

/**
 * Read the site's added ground materials.
 *
 * Every entry is validated, and a malformed one is *skipped* rather than fatal — unlike the terrain
 * or a plant, a material is additive: losing one costs the paint that used it, whereas refusing the
 * file costs the whole site. The channel is taken as stored and never reassigned, because the paint
 * map that travels alongside refers to materials by channel number; a channel the palette rejects
 * simply leaves that paint unreachable, which is visible and recoverable, where a silently
 * renumbered one would repaint the garden with the wrong material.
 */
function deserializeSurfacePalette(raw: unknown): SurfacePaletteState {
  if (typeof raw !== 'object' || raw === null) return { layers: [] };
  const entries = (raw as Record<string, unknown>).layers;
  if (!Array.isArray(entries)) return { layers: [] };

  const layers: SurfaceLayerState[] = [];
  for (const entry of entries) {
    if (typeof entry !== 'object' || entry === null) continue;
    const record = entry as Record<string, unknown>;
    const id = text(record.id);
    const channel = Math.trunc(Number(record.channel));
    const color = normalizeHex(record.color);
    if (!id || !color) continue;
    if (!Number.isFinite(channel) || channel < 0 || channel >= MAX_PAINTED_LAYERS) continue;

    const layer: SurfaceLayerState = {
      id,
      name: text(record.name) ?? id,
      channel,
      color,
      roughness: clamp(Number(record.roughness) || 0.85, 0, 1),
      plantable: record.plantable !== false,
      hint: text(record.hint) ?? '',
    };

    const swatch = record.swatch;
    if (typeof swatch === 'object' && swatch !== null) {
      const dataUrl = text((swatch as Record<string, unknown>).dataUrl);
      // Only a data URL: a swatch pointing at a remote address would make the document depend on a
      // host staying up, and would give a project file the power to make the app fetch a URL.
      if (dataUrl?.startsWith('data:image/')) {
        layer.swatch = {
          dataUrl,
          tileMeters: clamp(Number((swatch as Record<string, unknown>).tileMeters) || 1.5, 0.05, 40),
        };
      }
    }

    const character = text(record.character);
    if (character) layer.character = character;

    const origin = record.origin;
    if (typeof origin === 'object' && origin !== null) {
      const source = origin as Record<string, unknown>;
      const request = text(source.request);
      if (request) {
        const grounding = text(source.grounding);
        layer.origin = {
          kind: source.kind === 'uploaded' ? 'uploaded' : 'forged',
          request,
          ...(grounding ? { grounding } : {}),
          createdAt: text(source.createdAt) ?? new Date().toISOString(),
        };
      }
    }

    layers.push(layer);
  }
  return { layers };
}

/* --------------------------------- Terrain --------------------------------- */

function serializeTerrainState(state: TerrainProjectState): TerrainProjectState {
  return {
    generation: { ...state.generation },
    units: state.units,
    ...(state.bakedHeights ? { bakedHeights: [...state.bakedHeights] } : {}),
    ...(state.surface ? { surface: { ...state.surface } } : {}),
  };
}

function deserializeTerrainState(raw: unknown): TerrainProjectState {
  const data = asRecord(raw, 'terrain');
  const generation = asRecord(data.generation, 'terrain.generation');

  const templateId = generation.templateId;
  if (!TERRAIN_TEMPLATE_IDS.includes(templateId as TerrainTemplateId)) {
    throw new ProjectFormatError(`Unknown terrain template: ${String(templateId)}`);
  }

  const params: TerrainGenerationParams = {
    templateId: templateId as TerrainTemplateId,
    seed: Math.trunc(finite(generation.seed, 'terrain.generation.seed')),
    widthMeters: clamp(finite(generation.widthMeters, 'terrain.generation.widthMeters'), 1, 1000),
    lengthMeters: clamp(finite(generation.lengthMeters, 'terrain.generation.lengthMeters'), 1, 1000),
    segments: clamp(Math.trunc(finite(generation.segments, 'terrain.generation.segments')), 4, 512),
    elevationAmplitude: clamp(finite(generation.elevationAmplitude, 'terrain.generation.elevationAmplitude'), 0, 100),
    roughness: clamp(finite(generation.roughness, 'terrain.generation.roughness'), 0, 1),
    slopeDirection: finite(generation.slopeDirection, 'terrain.generation.slopeDirection'),
    slopeRiseMeters: finite(generation.slopeRiseMeters, 'terrain.generation.slopeRiseMeters'),
    northDegrees: finite(generation.northDegrees, 'terrain.generation.northDegrees'),
  };

  const units: UnitSystem = data.units === 'metric' ? 'metric' : 'imperial';
  const baked = data.bakedHeights;
  // Once the sculpt tool exists these are not an optimisation — they are the only record of ground
  // the generator cannot reproduce, so they are read back verbatim.
  const bakedHeights = Array.isArray(baked) ? baked.map((h) => Number(h) || 0) : undefined;
  const surface = deserializeSurfaceState(data.surface);

  return {
    generation: params,
    units,
    ...(bakedHeights ? { bakedHeights } : {}),
    ...(surface ? { surface } : {}),
  };
}

/**
 * Read the painted ground, or `undefined` when the field is absent or unreadable.
 *
 * Unlike the structural fields, a broken splat does not throw. A project whose paint failed to parse
 * is still a project — every plant, every building, every dimension is intact — and refusing to open
 * it would trade a recoverable cosmetic loss for a total one. The rebuild is defensive on the other
 * side too: `deserializeSurfaceMap` yields an unpainted site rather than an exception.
 */
function deserializeSurfaceState(raw: unknown): TerrainSurfaceState | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const data = raw as Record<string, unknown>;
  if (data.encoding !== 'rle-base64' || typeof data.data !== 'string') return undefined;
  const width = Math.trunc(Number(data.width));
  const height = Math.trunc(Number(data.height));
  if (!Number.isFinite(width) || !Number.isFinite(height) || width < 1 || height < 1) return undefined;
  if (width > MAX_SURFACE_RESOLUTION || height > MAX_SURFACE_RESOLUTION) return undefined;

  // The page count decides how many bytes each run carries, so dropping it does not degrade the
  // paint — it makes the run stream undecodable, and the whole splat is discarded as corrupt.
  // Absent means one page, which is every file written before the palette could grow.
  const pages = Math.trunc(Number(data.pages ?? 1));
  if (!Number.isFinite(pages) || pages < 1 || pages > MAX_SPLAT_PAGES) return undefined;

  return { width, height, pages, encoding: 'rle-base64', data: data.data };
}

/* ---------------------------------- Flora ---------------------------------- */

function serializeFloraInstance(instance: FloraInstance): FloraInstance {
  const out: FloraInstance = {
    ...instance,
    position: { ...instance.position },
    rotation: { ...instance.rotation },
  };
  // An override block is written only when it holds an actual decision, so a plant nobody has
  // overridden writes the record it always wrote and a document round-trips byte-identically.
  if (hasSiteOverrides(instance.siteOverrides)) out.siteOverrides = { ...instance.siteOverrides };
  else delete out.siteOverrides;
  return out;
}

function deserializeFloraInstance(raw: unknown, index: number): FloraInstance {
  const where = `floraInstances[${index}]`;
  const data = asRecord(raw, where);

  const id = text(data.id);
  const definitionId = text(data.floraDefinitionId);
  if (!id) throw new ProjectFormatError(`${where} is missing an id.`);
  if (!definitionId) throw new ProjectFormatError(`${where} is missing a plant type.`);

  const instance: FloraInstance = {
    id,
    floraDefinitionId: definitionId,
    position: vec3(data.position, `${where}.position`),
    rotation: vec3(data.rotation, `${where}.rotation`),
    maturity: clamp(number(data.maturity, 0.5), 0, MAX_OVERGROWN_MATURITY),
    visualSeed: Math.trunc(number(data.visualSeed, 0)),
    health: clamp(number(data.health, 1), 0, 1),
    fullness: clamp(number(data.fullness, 1), 0, 1),
    seasonalOffsetDays: Math.trunc(clamp(number(data.seasonalOffsetDays, 0), -365, 365)),
    sizeVariation: clamp(number(data.sizeVariation, 0), -1, 1),
    colorVariation: clamp(number(data.colorVariation, 0), -1, 1),
    pruningStyle: PRUNING_STYLES.includes(data.pruningStyle as PruningStyle)
      ? (data.pruningStyle as PruningStyle)
      : 'natural',
    locked: data.locked === true,
    hidden: data.hidden === true,
  };

  // Optional fields stay absent when unset — "no override" is distinct from "override of 0".
  const containerStage = text(data.containerStage);
  if (containerStage) instance.containerStage = containerStage;
  if (data.seasonOverrideDayOfYear !== undefined && data.seasonOverrideDayOfYear !== null) {
    instance.seasonOverrideDayOfYear = Math.trunc(
      clamp(number(data.seasonOverrideDayOfYear, 1), 1, 365),
    );
  }
  const aliasGroupId = text(data.aliasGroupId);
  if (aliasGroupId) instance.aliasGroupId = aliasGroupId;
  const groupId = text(data.groupId);
  if (groupId) instance.groupId = groupId;

  const siteOverrides = deserializeSiteOverrides(data.siteOverrides);
  if (siteOverrides) instance.siteOverrides = siteOverrides;

  return instance;
}

/**
 * Read a plant's explicit site overrides, or undefined when it has none.
 *
 * Every field stays absent unless the file actually carried it: `irrigated: false` written back for a
 * plant nobody irrigated would turn "not decided" into "decided against", and the resolver treats
 * those differently — an absent flag lets a site-wide irrigation setting apply, a false one would not.
 */
function deserializeSiteOverrides(raw: unknown): PlantSiteOverrides | undefined {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return undefined;
  const data = raw as Record<string, unknown>;
  const overrides: PlantSiteOverrides = {};

  if (data.irrigated === true) overrides.irrigated = true;
  if (data.protectedMicroclimate === true) overrides.protectedMicroclimate = true;
  if (data.containerPlanted === true) overrides.containerPlanted = true;

  const height = Number(data.managedHeightMeters);
  if (Number.isFinite(height) && height > 0) overrides.managedHeightMeters = clamp(height, 0.01, 150);
  const spread = Number(data.managedSpreadMeters);
  if (Number.isFinite(spread) && spread > 0) overrides.managedSpreadMeters = clamp(spread, 0.01, 150);
  const scale = Number(data.growthScale);
  if (Number.isFinite(scale) && scale > 0) overrides.growthScale = clamp(scale, 0.1, 5);

  return hasSiteOverrides(overrides) ? overrides : undefined;
}

/* ------------------------------- Site objects ------------------------------- */

function serializeSiteObject(object: SiteObject): SiteObject {
  const out: SiteObject = { ...object, position: { ...object.position } };
  // Deep-copy the shape so a stored document can never alias live editor state, and omit both
  // optional fields when absent so a rectangular building still writes a schema-3-shaped record.
  if (object.footprint) out.footprint = object.footprint.map((point) => ({ ...point }));
  else delete out.footprint;
  if (object.masses) {
    out.masses = object.masses.map((mass) => ({
      ...mass,
      footprint: mass.footprint.map((point) => ({ ...point })),
    }));
  } else delete out.masses;
  // Omitted when absent so a procedural building still writes the record it always wrote, and a
  // document that predates props is byte-identical after a round trip.
  if (object.modelId) out.modelId = object.modelId;
  else delete out.modelId;
  return out;
}

/**
 * Schema 1 → 2: site objects did not exist, so a v1 file has none.
 *
 * That is a migration, not a default — "this document predates buildings" and "this document has an
 * empty list of buildings" happen to produce the same result, but only the first is a statement we
 * can make honestly about a v1 file. Written out explicitly so the next migration has a pattern to
 * follow (`docs/PROJECT_FILE_FORMAT.md` § Schema Migration).
 */
/**
 * Schema 8 → 9: fauna did not exist, so a v8 file has none.
 *
 * The same statement `migrateSiteObjects` makes about a v1 file's buildings, and made the same way:
 * "this document predates fauna" and "this document's creatures were all deleted" would both produce
 * an empty list, and only the first is honest about a v8 file. Written out rather than defaulted so
 * the distinction survives the next person reading it.
 */
function migrateFaunaInstances(schemaVersion: number, raw: unknown): FaunaInstance[] {
  if (schemaVersion < 9) return [];
  if (raw === undefined || raw === null) return [];
  return asArray(raw, 'faunaInstances').map((entry, index) => deserializeFaunaInstance(entry, index));
}

function serializeFaunaInstance(instance: FaunaInstance): FaunaInstance {
  const out: FaunaInstance = { ...instance, position: { ...instance.position } };
  // Absent rather than written as undefined, so a creature that belongs to no flock round-trips as
  // the record it was.
  if (instance.groupId === undefined) delete out.groupId;
  return out;
}

function deserializeFaunaInstance(raw: unknown, index: number): FaunaInstance {
  const where = `faunaInstances[${index}]`;
  const data = asRecord(raw, where);

  const id = text(data.id);
  const definitionId = text(data.faunaDefinitionId);
  if (!id) throw new ProjectFormatError(`${where} is missing an id.`);
  if (!definitionId) throw new ProjectFormatError(`${where} is missing a fauna type.`);

  // A pose id is *not* validated against the catalog here. The serializer has no catalog, and it
  // should not: a document naming a pose this build dropped must still open, with the creature
  // falling back to its record's default pose (`FaunaFactory.poseOf`) rather than failing the load.
  const instance: FaunaInstance = {
    id,
    faunaDefinitionId: definitionId,
    position: vec3(data.position, `${where}.position`),
    rotationY: number(data.rotationY, 0),
    poseId: text(data.poseId) ?? '',
    attachment: FAUNA_ATTACHMENTS.includes(data.attachment as FaunaAttachment)
      ? (data.attachment as FaunaAttachment)
      : 'ground',
    heightOffsetMeters: clamp(number(data.heightOffsetMeters, 0), -100, 100),
    sizeVariation: clamp(number(data.sizeVariation, 0), -1, 1),
    visualSeed: Math.trunc(number(data.visualSeed, 0)),
    locked: data.locked === true,
    hidden: data.hidden === true,
  };

  const groupId = text(data.groupId);
  if (groupId) instance.groupId = groupId;
  return instance;
}

function migrateSiteObjects(schemaVersion: number, raw: unknown): SiteObject[] {
  if (schemaVersion < 2) return [];
  if (raw === undefined || raw === null) return [];
  return asArray(raw, 'siteObjects').map((entry, index) => deserializeSiteObject(entry, index));
}

function deserializeSiteObject(raw: unknown, index: number): SiteObject {
  const where = `siteObjects[${index}]`;
  const data = asRecord(raw, where);

  const id = text(data.id);
  if (!id) throw new ProjectFormatError(`${where} is missing an id.`);

  const kind: SiteObjectKind = SITE_OBJECT_KINDS.includes(data.kind as SiteObjectKind)
    ? (data.kind as SiteObjectKind)
    : 'building';

  const object: SiteObject = {
    id,
    kind,
    name: text(data.name) ?? { building: 'Building', paving: 'Paving', prop: 'Prop' }[kind],
    position: vec3(data.position, `${where}.position`),
    rotationY: number(data.rotationY, 0),
    widthMeters: clampExtent(number(data.widthMeters, 1)),
    depthMeters: clampExtent(number(data.depthMeters, 1)),
    heightMeters: clamp(number(data.heightMeters, 2.4), 0, 200),
    roof: ROOF_SHAPES.includes(data.roof as RoofShape) ? (data.roof as RoofShape) : 'gable',
    roofRiseMeters: clamp(number(data.roofRiseMeters, 1), 0, 100),
    wallColor: hexColor(data.wallColor, '#b9b2a4'),
    roofColor: hexColor(data.roofColor, '#6d6f73'),
    paving: PAVING_MATERIALS.includes(data.paving as PavingMaterial)
      ? (data.paving as PavingMaterial)
      : 'flagstone',
    clearanceMeters: clamp(number(data.clearanceMeters, 0), 0, 50),
    locked: data.locked === true,
    hidden: data.hidden === true,
  };

  // Shape is optional at every level: no footprint means the width/depth rectangle, and no masses
  // means the single volume the flat fields describe. A ring that does not enclose area is dropped
  // rather than thrown on — the rectangle is always a valid reading of the same record.
  const footprint = ring(data.footprint);
  if (footprint) object.footprint = footprint;

  const masses = deserializeMasses(data.masses);
  if (kind === 'building' && masses.length > 0) object.masses = masses;

  // A model id is kept whether or not the asset is installed. Dropping ids we cannot currently
  // resolve would silently rewrite the user's document on open — a project shared without its asset
  // library would come back permanently stripped of its props rather than temporarily unstyled.
  const modelId = text(data.modelId);
  if (modelId) object.modelId = modelId;

  return syncExtents(object);
}

/** Read a polygon ring, or undefined when the value is absent, malformed, or degenerate. */
function ring(raw: unknown): Ring | undefined {
  if (!Array.isArray(raw)) return undefined;
  const points: Point2[] = [];
  for (const entry of raw) {
    // Accept both `{x, z}` and `[x, z]`: the first is what we write, the second is what a
    // hand-authored plan or a model reaching for the shortest form tends to produce.
    if (Array.isArray(entry) && entry.length >= 2) {
      points.push({ x: number(entry[0], 0), z: number(entry[1], 0) });
    } else if (entry && typeof entry === 'object') {
      const point = entry as Record<string, unknown>;
      points.push({ x: number(point.x, 0), z: number(point.z, 0) });
    }
  }
  return isValidRing(points) ? normalizeRing(points) : undefined;
}

function deserializeMasses(raw: unknown): BuildingMass[] {
  if (!Array.isArray(raw)) return [];
  const masses: BuildingMass[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const data = entry as Record<string, unknown>;
    const footprint = ring(data.footprint);
    if (!footprint) continue;
    masses.push(
      normalizeMass({
        footprint,
        heightMeters: clamp(number(data.heightMeters, 2.4), 0, 200),
        roof: ROOF_SHAPES.includes(data.roof as RoofShape) ? (data.roof as RoofShape) : 'gable',
        roofRiseMeters: clamp(number(data.roofRiseMeters, 1), 0, 100),
        ...(text(data.name) !== undefined ? { name: text(data.name) as string } : {}),
        ...(data.baseMeters !== undefined
          ? { baseMeters: clamp(number(data.baseMeters, 0), -200, 200) }
          : {}),
        ...(data.ridgeCrosswise === true ? { ridgeCrosswise: true } : {}),
      }),
    );
  }
  return masses;
}

/** Accept `#rgb`/`#rrggbb`, falling back rather than throwing — a bad colour is not a corrupt file. */
function hexColor(value: unknown, fallback: string): string {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(trimmed)) return trimmed;
  if (/^#[0-9a-f]{3}$/.test(trimmed)) {
    const [r, g, b] = [trimmed[1], trimmed[2], trimmed[3]];
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return fallback;
}

/* ------------------------------ Groups & views ------------------------------ */

function deserializeGroups(raw: unknown, instances: readonly FloraInstance[]): PlantGroup[] {
  if (raw === undefined || raw === null) return [];
  const known = new Set(instances.map((instance) => instance.id));
  const groups: PlantGroup[] = [];
  for (const [index, entry] of asArray(raw, 'groups').entries()) {
    const data = asRecord(entry, `groups[${index}]`);
    const id = text(data.id);
    if (!id) throw new ProjectFormatError(`groups[${index}] is missing an id.`);
    const memberIds = asArray(data.memberIds ?? [], `groups[${index}].memberIds`)
      .map((member) => text(member))
      .filter((member): member is string => member !== undefined && known.has(member));
    groups.push({ id, name: text(data.name) ?? `Group ${index + 1}`, memberIds });
  }
  return groups;
}

function deserializeCameraViews(raw: unknown): SavedCameraView[] {
  if (raw === undefined || raw === null) return [];
  return asArray(raw, 'cameraViews').map((entry, index) => {
    const data = asRecord(entry, `cameraViews[${index}]`);
    return {
      id: text(data.id) ?? `view-${index}`,
      name: text(data.name) ?? `View ${index + 1}`,
      position: vec3(data.position, `cameraViews[${index}].position`),
      target: vec3(data.target, `cameraViews[${index}].target`),
      fovDegrees: clamp(number(data.fovDegrees, 50), 1, 179),
    };
  });
}

/* ------------------------------- Semantic tags ------------------------------- */

/**
 * Schema 4 → 5: semantic tags did not exist, so a v4 file has none.
 *
 * Stated as a migration rather than left to a default, following the pattern `migrateSiteObjects`
 * set: "this document predates tagging" and "this document has been untagged" produce the same empty
 * list, but only the first is something we can honestly say about a v4 file.
 */
function migrateSemanticTags(
  schemaVersion: number,
  raw: unknown,
  instances: readonly FloraInstance[],
  siteObjects: readonly SiteObject[],
  palette: SurfacePaletteState,
): SemanticTag[] {
  if (schemaVersion < 5) return [];
  if (raw === undefined || raw === null) return [];

  const livePlantIds = new Set(instances.map((instance) => instance.id));
  const liveObjectIds = new Set(siteObjects.map((object) => object.id));
  // Materials this *document* has, which is not the same set as the palette currently installed:
  // the file is validated against itself, so opening it never depends on which site was open first.
  const liveLayerIds = new Set<string>([
    BASE_SURFACE_LAYER.id,
    ...BUILT_IN_PAINTED_LAYERS.map((layer) => layer.id),
    ...palette.layers.map((layer) => layer.id),
  ]);

  const tags: SemanticTag[] = [];
  for (const [index, entry] of asArray(raw, 'semanticTags').entries()) {
    const tag = deserializeSemanticTag(entry, index, livePlantIds, liveObjectIds, liveLayerIds);
    if (tag) tags.push(tag);
  }
  return tags;
}

/**
 * Read one tag, or undefined when it is unreadable or points at something the file does not contain.
 *
 * Unlike the structural fields, a bad tag never throws. A project whose annotations are damaged is
 * still a project — every plant, every dimension, every building is intact — and refusing to open it
 * would trade a recoverable loss of description for a total loss of the design. Dangling targets are
 * dropped for the same reason they are filtered on the way out: a tag about a plant that is not in
 * the document describes nothing and would silently pad every future render prompt.
 */
function deserializeSemanticTag(
  raw: unknown,
  index: number,
  livePlantIds: ReadonlySet<string>,
  liveObjectIds: ReadonlySet<string>,
  liveLayerIds: ReadonlySet<string>,
): SemanticTag | undefined {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return undefined;
  const data = raw as Record<string, unknown>;

  const id = text(data.id);
  const label = normalizeLabel(text(data.label) ?? '');
  if (!id || label.length === 0) return undefined;

  const target = deserializeTagTarget(data.target, livePlantIds, liveObjectIds, liveLayerIds);
  if (!target) return undefined;

  const tag: SemanticTag = {
    id,
    target,
    label,
    category: TAG_CATEGORIES.includes(data.category as TagCategory)
      ? (data.category as TagCategory)
      : 'identity',
    emphasis: TAG_EMPHASES.includes(data.emphasis as TagEmphasis)
      ? (data.emphasis as TagEmphasis)
      : 'mention',
    createdAt: text(data.createdAt) ?? new Date().toISOString(),
  };
  const note = normalizeNote(text(data.note));
  if (note !== undefined) tag.note = note;

  // Schema 8. Read without a version gate because absence and "this file predates images" are the
  // same bytes; the version's job here is to refuse a *newer* file, not to hide a field a v7 file
  // could not have written. Unreadable entries are dropped individually — a damaged picture must not
  // cost the sentence it was attached to.
  const images = parseTagImages(data.images);
  if (images.length > 0) tag.images = images;

  void index;
  return tag;
}

function deserializeTagTarget(
  raw: unknown,
  livePlantIds: ReadonlySet<string>,
  liveObjectIds: ReadonlySet<string>,
  liveLayerIds: ReadonlySet<string>,
): TagTarget | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const data = raw as Record<string, unknown>;

  switch (data.kind) {
    case 'site':
      return { kind: 'site' };
    case 'ground': {
      // Checked against the materials *this document* declares, not against the palette that
      // happens to be installed. Since a site can add its own materials, "gravel, mulch, water,
      // blacktop, grass" is no longer the whole answer — but a file that names a material it does
      // not carry is still describing nothing, and dropping it is the same rule as a dangling plant.
      const layer = text(data.layer);
      return layer && liveLayerIds.has(layer) ? { kind: 'ground', layer } : undefined;
    }
    case 'plant': {
      const instanceId = text(data.instanceId);
      return instanceId && livePlantIds.has(instanceId) ? { kind: 'plant', instanceId } : undefined;
    }
    case 'structure': {
      const objectId = text(data.objectId);
      return objectId && liveObjectIds.has(objectId) ? { kind: 'structure', objectId } : undefined;
    }
    case 'location':
      return {
        kind: 'location',
        x: number(data.x, 0),
        z: number(data.z, 0),
        radiusMeters: clamp(number(data.radiusMeters, 3), 0.25, MAX_TAG_RADIUS_METERS),
      };
    default:
      return undefined;
  }
}

/* --------------------------------- Helpers --------------------------------- */

function asRecord(value: unknown, where: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new ProjectFormatError(`Expected ${where} to be an object.`);
  }
  return value as Record<string, unknown>;
}

function asArray(value: unknown, where: string): unknown[] {
  if (!Array.isArray(value)) {
    throw new ProjectFormatError(`Expected ${where} to be a list.`);
  }
  return value;
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function number(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function finite(value: unknown, where: string): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    throw new ProjectFormatError(`Expected ${where} to be a number.`);
  }
  return parsed;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function vec3(value: unknown, where: string): Vec3 {
  const data = asRecord(value, where);
  return {
    x: finite(data.x, `${where}.x`),
    y: finite(data.y, `${where}.y`),
    z: finite(data.z, `${where}.z`),
  };
}
